import { FC, ReactNode, useState } from 'react';
import { beautifyTheLowHighObject, isNotEmpty, replaceEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { Radio } from 'antd';
import dayjs from 'dayjs';
import V2Table from '@/common/components/Data/V2Table';
import styles from './FlowTable.module.less';
import RankModal from './RankModal';

type FlowType = 'daily' | 'annual';

interface FlowTableProps {
  flowCard?: any;
  flowType: FlowType;
  latestMonth?: string;
  flowCoefficient?: number;
  flowCoefficientLoading: boolean;
  detail: any;
  /** 表格刷新标识 */
  tableRefreshKey: number;
  onFlowTypeChange: (event: any) => void;
  datePicker?: ReactNode;
  showModule?: boolean;
}

const ROW_CONFIG = [
  { key: 'flow', label: '客流' },
  { key: 'cityRank', label: '全市排名' },
  { key: 'nationRank', label: '全国排名' },
  { key: 'cityMedian', label: '城市客流中位数' },
  { key: 'tierMedian', label: '所属城市线级客流中位数' },
  { key: 'nationMedian', label: '全国客流中位数' },
];

/**
 * 商场客流表格，将接口返回的三列对象编排为表格行数据。
 */
const FlowTable: FC<FlowTableProps> = ({
  flowCard,
  flowType,
  latestMonth,
  flowCoefficient,
  flowCoefficientLoading,
  detail,
  tableRefreshKey,
  onFlowTypeChange,
  datePicker,
  showModule,
}) => {
  type RankScope = 'city' | 'nation';
  type FlowMetric = 'dailyAvg' | 'workday' | 'holiday';
  const [rankModalInfo, setRankModalInfo] = useState<{
    open: boolean;
    rankScope: RankScope;
    flowMetric: FlowMetric;
  }>({
    open: false,
    rankScope: 'city',
    flowMetric: 'dailyAvg',
  });
  const methods = useMethods({
    formatValue(value, rowKey) {
      // 数值展示：客流/中位数类按数值大小自动带「人次/万人次」单位，其余直接展示
      if (!isNotEmpty(value)) {
        return replaceEmpty(value);
      }
      if (rowKey === 'flow' || rowKey.includes('Median')) {
        const result = beautifyTheLowHighObject(1, value, 1, { low: '人次', high: '万人次' });
        return `${result.val}${result.unit}`;
      }
      return value;
    },
    buildTableData() {
      // 接口数据异步返回前不组装占位行，避免表格缓存空数据。
      if (!flowCard || (!flowCard.dailyAvg && !flowCard.workday && !flowCard.holiday)) {
        return [];
      }
      const dailyAvg = flowCard?.dailyAvg || {};
      const workday = flowCard?.workday || {};
      const holiday = flowCard?.holiday || {};
      // 将接口中「日均/工作日/节假日」三列对象展开为表格行：每行对应一个指标，三个数据列取值后额外附带全市/全国排名对应的总量
      return ROW_CONFIG.map((row) => ({
        key: row.key,
        label: row.key === 'flow' && latestMonth ? (flowType === 'daily' ? `${dayjs(latestMonth).format('M')}月客流` : '年日均客流') : row.label,
        dailyAvg: dailyAvg[row.key],
        workday: workday[row.key],
        holiday: holiday[row.key],
        dailyAvgCityTotal: dailyAvg.cityMallTotal,
        dailyAvgNationTotal: dailyAvg.nationMallTotal,
        workdayCityTotal: workday.cityMallTotal,
        workdayNationTotal: workday.nationMallTotal,
        holidayCityTotal: holiday.cityMallTotal,
        holidayNationTotal: holiday.nationMallTotal,
      }));
    },
    fetchTableData() {
      // 表格数据由接口数据本地组装，无需异步分页请求，此处同步构造后返回
      const dataSource = methods.buildTableData();
      return Promise.resolve({ dataSource, count: dataSource.length });
    },
    renderFlow(value, record, dataIndex) {
      const displayValue = methods.formatValue(value, record.key);
      // 全市/全国排名行渲染为可点击链接，剩余字段直接展示；点击可打开排名详情弹窗
      if (record.key !== 'cityRank' && record.key !== 'nationRank') {
        return displayValue;
      }
      const totalKey = record.key === 'cityRank' ? 'CityTotal' : 'NationTotal';
      const total = record[`${dataIndex}${totalKey}`];
      const rankText = isNotEmpty(total) ? `${displayValue} (总数${total})` : displayValue;
      return (
        <span
          className={styles.rankLink}
          data-rank-scope={record.key === 'cityRank' ? 'city' : 'nation'}
          data-flow-metric={dataIndex}
          onClick={methods.openRankModal}
        >
          {rankText}
        </span>
      );
    },
    renderDailyAvg(value, record) {
      return methods.renderFlow(value, record, 'dailyAvg');
    },
    renderWorkday(value, record) {
      return methods.renderFlow(value, record, 'workday');
    },
    renderHoliday(value, record) {
      return methods.renderFlow(value, record, 'holiday');
    },
    openRankModal(event) {
      if (flowCoefficientLoading || typeof flowCoefficient !== 'number') {
        return;
      }
      setRankModalInfo({
        open: true,
        rankScope: event.currentTarget.dataset.rankScope,
        flowMetric: event.currentTarget.dataset.flowMetric,
      });
    },
    closeRankModal() {
      setRankModalInfo((state) => ({ ...state, open: false }));
    },
  });

  // 表格列定义：首列为指标名，后三列按当前「日均/年均」切换表头展示对应数据
  const defaultColumns = [
    {
      key: 'label',
      title: '-',
      dataIndex: 'label',
      width: '28%',
      dragChecked: true,
    },
    {
      key: 'dailyAvg',
      title: flowType === 'daily' ? '日均客流' : '年均客流',
      dataIndex: 'dailyAvg',
      width: '24%',
      align: 'center',
      dragChecked: true,
      render: methods.renderDailyAvg,
    },
    {
      key: 'workday',
      title: '工作日客流',
      dataIndex: 'workday',
      width: '24%',
      align: 'center',
      dragChecked: true,
      render: methods.renderWorkday,
    },
    {
      key: 'holiday',
      title: '节假日客流',
      dataIndex: 'holiday',
      width: '24%',
      align: 'center',
      dragChecked: true,
      render: methods.renderHoliday,
    },
  ];

  const latestMonthDate = latestMonth
    ? dayjs(latestMonth.length === 6 ? `${latestMonth.slice(0, 4)}-${latestMonth.slice(4)}` : latestMonth)
    : undefined;
  const annualMonthRange = flowType === 'annual' && latestMonthDate?.isValid()
    ? `${latestMonthDate.subtract(12, 'month').format('YYYY.MM')}-${latestMonthDate.format('YYYY.MM')}`
    : '';

  return (
    <div className={styles.flowTableSection}>
      <div className={styles.flowTableToolbar}>
        <div>
          <Radio.Group
            value={flowType}
            onChange={onFlowTypeChange}
            className={styles.flowTypeSwitch}
          >
            <Radio.Button value='daily'>日均</Radio.Button>
            <Radio.Button value='annual'>年均</Radio.Button>
          </Radio.Group>
          {
            flowType === 'annual' && <span className='c-666 pl-12'>
              {annualMonthRange}
            </span>
          }
        </div>
        {datePicker}
      </div>
      {
        showModule && <V2Table
          key={tableRefreshKey}
          className={styles.flowTable}
          rowKey='key'
          defaultColumns={defaultColumns}
          onFetch={methods.fetchTableData}
          pagination={false}
          bordered
          emptyRender
          hideColumnPlaceholder
        />
      }
      <RankModal
        open={rankModalInfo.open}
        detail={detail}
        flowType={flowType}
        latestMonth={latestMonth}
        flowCoefficient={flowCoefficient ?? 1}
        rankScope={rankModalInfo.rankScope}
        flowMetric={rankModalInfo.flowMetric}
        onCancel={methods.closeRankModal}
      />
    </div>
  );
};

export default FlowTable;
